import { useQuery } from '@tanstack/react-query'
import type { AdminTopupQuery, AdminUserQuery, PaginationQuery, UsageQuery } from '@ytclip/shared'
import {
  fetchAdminTopups,
  fetchAdminUsers,
  fetchMenu,
  fetchSettings,
  fetchTopupReport,
} from './admin/api.ts'
import { fetchBalance } from './balance/api.ts'
import { fetchMembership } from './membership/api.ts'
import { fetchModels } from './models/api.ts'
import { fetchTransactions } from './transactions/api.ts'
import { fetchUsageLogs } from './usage/api.ts'

/** Balance changes after a payment, so it is refreshed on focus. */
export function useBalance() {
  return useQuery({
    queryKey: ['balance'],
    queryFn: fetchBalance,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  })
}

export function useModels() {
  return useQuery({ queryKey: ['models'], queryFn: fetchModels, staleTime: 5 * 60_000 })
}

export function useUsageLogs(query: UsageQuery) {
  return useQuery({
    queryKey: ['usage', query],
    queryFn: () => fetchUsageLogs(query),
    staleTime: 30_000,
  })
}

export function useTransactions(query: PaginationQuery) {
  return useQuery({
    queryKey: ['transactions', query],
    queryFn: () => fetchTransactions(query),
    staleTime: 15_000,
  })
}

export function useMembership() {
  return useQuery({ queryKey: ['membership'], queryFn: fetchMembership, staleTime: 60_000 })
}

export function useAdminSettings() {
  return useQuery({ queryKey: ['admin-settings'], queryFn: fetchSettings })
}

export function useAdminUsers(query: AdminUserQuery) {
  return useQuery({
    queryKey: ['admin-users', query],
    queryFn: () => fetchAdminUsers(query),
    staleTime: 15_000,
  })
}

export function useTopupReport() {
  return useQuery({ queryKey: ['admin-topup-report'], queryFn: fetchTopupReport, staleTime: 30_000 })
}

export function useAdminTopups(query: AdminTopupQuery) {
  return useQuery({
    queryKey: ['admin-topups', query],
    queryFn: () => fetchAdminTopups(query),
    staleTime: 15_000,
  })
}

export function useAdminMenu() {
  return useQuery({ queryKey: ['admin-menu'], queryFn: fetchMenu })
}
